import {FormProvider, useForm} from "react-hook-form";
import {CheckoutFormFormValues} from "../../../../../components/checkout-form/form/types";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert, Button, Col, message, Row, Space, Input, Tooltip} from "antd";
import {StyledServiceButtonWrapper} from "../../../../../styles/onboarding/onboarding";
import {useCreateProductContext} from "../../context/create-product";
import {useCreateEditProductHook} from "../../hooks/create-product";
import {CreateProductFormValues} from "../../reducers/create-product/types";
import {offeringCheckoutFormValidationSchema} from "../../../../../components/offering-form/general/checkout-form/validation-schema";
import {OfferingFormCheckoutFormValues} from "../../../../../components/offering-form/general/checkout-form/types";
import {ProductCheckoutFormFields} from "../../../../../components/offering-form/products/checkout-form";
import {useNavigate} from "react-router-dom";
import {CustomModal} from "../../../../../components/modal";
import {REBRAND_BASE_URL} from "../../../../../services/config";
import {StyledModalContentWrapper} from "../../../../../styles/transactions/transactions";
import {StyledSuccessServiceContent} from "../../../../../components/calls/service/service-form/service-form.style";
import {handleProductLink} from "../../../../../utils/products";
import {useGetProfileContext} from "../../../../../context/profile/profile-context";
import {
  CopyOutlined,
  FacebookFilled,
  MailFilled,
  TwitterOutlined,
} from "@ant-design/icons";
import {useState} from "react";
import {
  OFFERING_CREATE_DRAFT_KEYS,
  useClearOfferingCreateDraft,
  useSyncCheckoutFormToContext,
} from "../../../../../utils/offering-create-draft";

export function CreateProductCheckoutFormStep() {
  const navigate = useNavigate();
  const [isSuccessBecauseEmpty, setIsSuccessBecauseEmpty] = useState(false);
  const {state: profileState} = useGetProfileContext();
  const {
    state: {formData, apiResponseData},
    dispatch,
  } = useCreateProductContext();
  const clearProductDraft = useClearOfferingCreateDraft(
    OFFERING_CREATE_DRAFT_KEYS.product
  );
  const checkoutFormMethods = useForm<OfferingFormCheckoutFormValues>({
    resolver: yupResolver(offeringCheckoutFormValidationSchema),
    mode: "onChange",
    defaultValues: formData,
  });
  useSyncCheckoutFormToContext({
    checkoutFormMethods,
    dispatch,
    saveActionType: "save-create-product-form-values",
    formData: {
      bookingQuestions: formData.bookingQuestions ?? [],
    },
  });
  const {createEditProduct, wasProductCreateEdited, isCreateServiceEditing} =
    useCreateEditProductHook();
  function onSubmit(values: CheckoutFormFormValues) {
    if (apiResponseData) {
      const payload: CreateProductFormValues = {
        ...formData,
        ...values,
      };
      if (values.bookingQuestions.length > 0) {
        setIsSuccessBecauseEmpty(false);
        createEditProduct(payload, () => {});
      } else {
        setIsSuccessBecauseEmpty(true);
      }
    } else {
      message.error("No service id found. Go back to step 1");
    }
  }

  const handleSuccessModalOk = () => {
    clearProductDraft();
    navigate("/products?activeTab=digital-products");
  };

  const productBookingLink = handleProductLink({
    productId: apiResponseData?.product?.id ?? "",
    slug: apiResponseData?.product?.slug ?? "",
    username: profileState.profileData.username,
    tinyUrl: apiResponseData?.product?.tiny_url ?? "",
    windowNav: true,
  });

  const isFormError = Object.keys(checkoutFormMethods.formState.errors).length > 0;
  const isBookingQuestionsError = checkoutFormMethods.formState.errors.bookingQuestions;

  return (
    <>
      <FormProvider {...checkoutFormMethods}>
        <form onSubmit={checkoutFormMethods.handleSubmit(onSubmit)} autoComplete="off">
          <Space size={16} direction="vertical" style={{display: "flex"}}>
            <ProductCheckoutFormFields
              isBookingQuestionsError={!!isBookingQuestionsError}
            />
            {isFormError && (
              <Alert
                message="Please fill in the required fields above, outlined in red"
                type="warning"
              />
            )}
            <Row gutter={[0, 16]}>
              <Col span={24} sm={12}>
                <StyledServiceButtonWrapper>
                  <Button
                    className="secondary-btn"
                    onClick={() => {
                      dispatch({
                        type: "save-create-product-form-values",
                        payload: checkoutFormMethods.getValues(),
                      });
                      dispatch({
                        type: "update-create-product-form-step",
                        payload: "automations",
                      });
                    }}
                    block
                    size="small"
                    type="default"
                  >
                    Back
                  </Button>
                  <Button
                    block
                    type="primary"
                    htmlType="submit"
                    size="small"
                    loading={isCreateServiceEditing}
                    disabled={
                      Object.keys(checkoutFormMethods.formState.errors).length > 0 ||
                      Object.values(checkoutFormMethods.watch()).length < 1
                    }
                  >
                    Submit
                  </Button>
                </StyledServiceButtonWrapper>
              </Col>
            </Row>
          </Space>
        </form>
      </FormProvider>
      {(wasProductCreateEdited || isSuccessBecauseEmpty) && (
        <CustomModal
          visibleState={true}
          title="Product created"
          okFunc={handleSuccessModalOk}
          cancelFunc={handleSuccessModalOk}
          footerContent={[
            <Button
              key="publicProfile"
              type="primary"
              href={`${REBRAND_BASE_URL}/${profileState.profileData.username}`}
              target="_blank"
              size="small"
            >
              View my public profile
            </Button>,
            <Button
              size="small"
              key="gotIt"
              type="default"
              onClick={handleSuccessModalOk}
            >
              Got it!
            </Button>,
          ]}
        >
          <StyledModalContentWrapper>
            <StyledSuccessServiceContent>
              <p className="success-text">
                Your product has been successfully created! Share your product link to
                start making sales.
              </p>
              <div className="success-share-info">
                <Input.Group compact style={{display: "flex"}}>
                  <Input size="large" readOnly defaultValue={productBookingLink} />
                  <Tooltip title={"Copy product url"}>
                    <Button
                      size="small"
                      icon={<CopyOutlined />}
                      onClick={() =>
                        navigator.clipboard
                          .writeText(productBookingLink)
                          .then(() => message.success("Copied"))
                      }
                    />
                  </Tooltip>
                </Input.Group>
                <p>or</p>
                <Space direction="horizontal" size={16}>
                  <div>
                    <Button
                      shape="circle"
                      size="small"
                      icon={<FacebookFilled />}
                      style={{
                        backgroundColor: "#EBEEF5",
                        color: "#3B5998",
                        border: "none",
                      }}
                      type="link"
                      target="_blank"
                      href={`https://www.facebook.com/sharer/sharer.php?u=${productBookingLink}&amp`}
                      title="Share on facebook"
                    />
                  </div>
                  <div>
                    <Button
                      title="Share on twitter"
                      shape="circle"
                      size="small"
                      icon={<TwitterOutlined />}
                      target="_blank"
                      style={{
                        backgroundColor: "#E6F7FD",
                        color: "#00ACEE",
                        border: "none",
                      }}
                      href={`https://twitter.com/intent/tweet?text=I just created a product titled "${apiResponseData?.product?.title}" on Coachli. Purchase my product on Coachli!&url=${productBookingLink}&hashtags=coachli,useCoachli`}
                    />
                  </div>
                  <div>
                    <Button
                      title="Send mail"
                      shape="circle"
                      size="small"
                      icon={<MailFilled />}
                      style={{
                        backgroundColor: "#FBECEB",
                        color: "#DB4437",
                        border: "none",
                      }}
                      href={`mailto:?subject=I wanted you to see this site&amp;body=I created a product ${productBookingLink}`}
                    />
                  </div>
                </Space>
              </div>
            </StyledSuccessServiceContent>
          </StyledModalContentWrapper>
        </CustomModal>
      )}
    </>
  );
}
